import { useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Notify } from 'react-vant';
import { isSigned, web3auth } from '../service';

import moment from "moment"
import detectEthereumProvider from "@metamask/detect-provider";

export function useWallet() {
  const [ account, setAccount ] = useState(null);
  const [ isConnected, setConnected ] = useState(false);
  const [ isUninstall, setUninstall ] = useState(false);
  const [ provider, setProvider ] = useState(null);

  // Web3
  const connect = useCallback(() => {
    if (provider) {
      provider.request({ method: 'eth_requestAccounts' }).catch((err) => {
        if (err.code === 4001) {
          return setConnected(false);
        }

        Notify.show({
          type: 'warning',
          message: err.message,
        });
      });
    }
  }, [ provider, setConnected ])

  const handleAccountsChanged = useCallback(accounts => {
    if (accounts.length === 0) {
      return setConnected(false);
    }
    setConnected(true);
    setAccount(accounts[0]);
  }, [ setAccount, setConnected ]);

  const identify = useCallback(() => {
    if (provider) {
      provider.request({ method: 'eth_accounts' })
        .then(handleAccountsChanged)
        .catch(err => {
          Notify.show({
            type: 'warning',
            message: err.message,
          });
        });
    }
  }, [ provider ])

  // Session
  const session = useQuery({
    queryKey: ['admin session'],
    queryFn: () => isSigned().then(res => {
      return res.code === 200 && res.data;
    }),
  });

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params) => web3auth(params),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['admin session'] })
    },
  });

  const str2hex = (str) => {
    let buffer = [...new TextEncoder().encode(str)];
    let hex = buffer.map(b => b.toString(16).padStart(2, '0')).join('');
    return '0x'+hex;
  }

  const format_address = (account) => {
    return `${account.slice(0, 5)}...${account.slice(-4)}`;
  }

  const doLogin = useCallback(() => {
    let msg = `   Admin: ${format_address(account)}\nLogin at: ${moment()}`;

    let run = async () => {
      let sig = await provider.request({
        method: 'personal_sign',
        params: [ str2hex(msg), account ],
      }).catch(err => err);

      if (sig instanceof Error || sig.code === 4001) {
        return Notify.show({
          type: 'warning',
          message: sig.message,
        });
      }

      mutation.mutate({ msg, sig });
    }

    run();
  }, [ provider, account, mutation ])

  useEffect(() => {
      if (mutation.isError) {
        return Notify.show({
          type: 'warning',
          message: mutation.error.message,
        });
      }

      if (mutation.data && mutation.data.code !== 200) {
        return Notify.show({
          type: 'warning',
          message: mutation.data.error,
        });
      }
  }, [mutation]);

  useEffect(() => {
    if (provider) {
      provider.on('accountsChanged', handleAccountsChanged);

      if (provider !== window.ethereum) {
        Notify.show({ type: 'warning', message: 'Do you have multiple wallets installed?' })
      }
    }
  }, [ provider ]);

  useEffect(() => {
    detectEthereumProvider().then(provider => {
      if (provider) {
        setProvider(provider);
        identify();
      } else {
        setUninstall(true);
      }
    })

    return () => {}
  }, [ identify, setUninstall, setProvider ])

  return { provider, account, isConnected, isUninstall, connect, session, doLogin };
}
